import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DashboardLayout from '../src/layouts/DashboardLayout';
import RoadwayPage from './components/Roadway/Page_Roadway';
import ColumnPage from './components/Column/Page_Column';
import Login from './components/Login/login.component';
import UpdatePassword from './components/Login/update-password.component';
import ForgotPassword from './components/Login/forget-password.component';
import NewPassword from './components/Login/new-password.component';
import Dashboard from './components/Dashboard/dashboard.component';
import Visualize from './visualize/visualize.component';
import CanvasImage from './plan/plan-dashboard.component';
import Launcher from './components/launcher/launcher.component';
import AutomateDashboard from './automate/automate-dashboard.component';
import AutomateUpload from './automate/automate-upload.component';
import AdminDashboard from './admin/admin-dashboard.component';
import PhotometricViewer from './components/Luminaire/Hierarchy/photometry-viewer.component';
import PhotometricDashboard from './photometricManager/manageComponents/photometric-dashboard.component';
import MonitorDashboard from './monitor/monitor-dashboard.component';
import UserAdminDashboard from './userAdministrate/userAdministrate-dashboard.component';
import PhotometricSearch from './photometricManager/photometric-search.component';
import PhotometricTransferList from './photometricManager/photometric-transfer-list.component';
import PhotometricUpload from './photometricManager/manageComponents/photometric-upload.component';
import PhotometricInformation from './photometricManager/photometric-information.component';
import PlanDashboard from './plan/plan-dashboard.component';
import SettingsView from './components/Settings/index';
import SupportView from './components/Support/index';
import Sandbox from './plan/sandbox.component';
import TwoFactorAuth from './components/Login/two-factor-auth.component';
import SVGTest from './plan/svgtest.component';
import Loading from './components/shared/loading.component';
import { getUserAdminAccess } from './authorization/userAdminAccess';
import UserAdminRedirect from './authorization/UserAdminRedirect';


function getLastPath() {
  const allowedPaths = [
    "/optimize",
    "/automate",
    "/photometry",
    "/plan",
    "/design",
  ];
  const lastPath = window.localStorage.getItem("lastPath");
  if (lastPath && allowedPaths.some(path => lastPath?.includes(path))) {
    window.localStorage.setItem("redirectedFromLauncher", "true");
    return <Navigate to={lastPath} />;
  }
  return <Launcher />;
}

function getProtectedElement(isLoggedIn, is2FAAuthed, element) {
  if (isLoggedIn && is2FAAuthed) {
    return element;
  }

  return <Navigate to={isLoggedIn ? "/2fa-setup" : "/login"} />;
}

function UserAdminProtectedElement({ isLoggedIn, is2FAAuthed, children }) {
  const userAdminAccess = useSelector((state) => getUserAdminAccess(state.user));

  if (!isLoggedIn || !is2FAAuthed) {
    return <Navigate to={isLoggedIn ? "/2fa-setup" : "/login"} />;
  }

  if (userAdminAccess.isLoading) {
    return <Loading type="backdrop" open text="Checking access" />;
  }

  if (!userAdminAccess.isAuthorized) {
    return <UserAdminRedirect />;
  }

  return children;
}

const routes = (isLoggedIn, is2FAAuthed) => [
  {
    path: '/',
    element: !isLoggedIn ? <Navigate to="/login" /> : <Navigate to={is2FAAuthed ? "/launcher" : "/2fa-setup"} />
  },
  {
    path: '/login',
    element: !isLoggedIn ? <Login /> : <Navigate to={is2FAAuthed ? "/launcher" : "/2fa-setup"} />,
  },
  {
    path: '/forgot',
    element: <ForgotPassword />
  },
  {
    path: '/resetPassword',
    element: <NewPassword />
  },
  {
    path: '/updatePassword',
    element: !isLoggedIn ? <Login /> : <UpdatePassword />
  },
  {
    path: 'automate',
    element: getProtectedElement(isLoggedIn, is2FAAuthed, <DashboardLayout app={"Automate"} />),
    children: [
      {
        path: '/',
        element: <AutomateDashboard />,
        children: [
          { path: 'upload', element: <AutomateUpload /> },
        ]
      },
      {
        path: '',
        element: <AutomateDashboard />,
        children: [
          { path: 'upload', element: <AutomateUpload /> },
        ]
      },
      { path: 'settings', element: <SettingsView /> },
    ]
  },
  {
    path: 'plan',
    element: getProtectedElement(isLoggedIn, is2FAAuthed, <DashboardLayout app={"Plan"} />),
    children: [
      {
        path: '/',
        element: <PlanDashboard />,
      },
    ]
  },
  {
    path: 'design',
    element: getProtectedElement(isLoggedIn, is2FAAuthed, <DashboardLayout app={"Design"} />),
    children: [
      {
        path: '/',
        element: <PlanDashboard />,
      },
    ]
  },
  {
    path: 'optimize',
    element: getProtectedElement(isLoggedIn, is2FAAuthed, <DashboardLayout app={"Optimize"} />),
    children: [
      {
        path: '/',
        element: <Dashboard />,
        children: [
          { path: 'row', element: <ColumnPage /> },
          { path: 'roadway', element: <RoadwayPage /> },
          { path: 'photometry', element: <PhotometricViewer /> },
        ]
      },
    ]
  },
  {
    path: 'photometry',
    element: getProtectedElement(isLoggedIn, is2FAAuthed, <DashboardLayout app={"Photometric Manager"} />),
    children: [
      {
        path: '/',
        element: <PhotometricDashboard />,
        children: [
          { path: 'search', element: <PhotometricSearch /> },
          { path: 'upload', element: <PhotometricUpload /> },
          { path: 'information', element: <PhotometricInformation /> },
          { path: 'groups', element: <PhotometricTransferList /> }
        ]
      },
      {
        path: 'photometry/',
        element: <PhotometricDashboard />,
        children: [
          { path: 'search', element: <PhotometricSearch /> },
          { path: 'upload', element: <PhotometricUpload /> },
          { path: 'information', element: <PhotometricInformation /> },
          { path: 'groups', element: <PhotometricTransferList /> }
        ]
      },
    ]
  },
  {
    path: 'hidden/',
    element: getProtectedElement(isLoggedIn, is2FAAuthed, <Dashboard />),
    children: [
      { path: 'visualize', element: <Visualize /> },
      { path: 'canvas', element: <CanvasImage /> }
    ]
  },
  {
    path: 'supersecret/admin/123',
    element: getProtectedElement(isLoggedIn, is2FAAuthed, <AdminDashboard />)
  },
  {
    path: '/launcher',
    element: (isLoggedIn && is2FAAuthed) ? getLastPath() : <Navigate to={isLoggedIn ? "/2fa-setup" : "/login"} />
  },
  {
    path: 'monitor',
    element: getProtectedElement(isLoggedIn, is2FAAuthed, <DashboardLayout app={"Monitor"} />),
    children: [
      {
        path: '/',
        element: <MonitorDashboard />,
      },
    ]
  },
  {
    path: 'userAdmin',
    element: (
      <UserAdminProtectedElement isLoggedIn={isLoggedIn} is2FAAuthed={is2FAAuthed}>
        <DashboardLayout app={"UserAdmin"} />
      </UserAdminProtectedElement>
    ),
    children: [
      {
        path: '/',
        element: <UserAdminDashboard />,
      },
    ]
  },
  {
    path: '/support',
    element: <SupportView />
  },
  {
    path: '/sandbox',
    element: <Sandbox />
  },
  {
    path: '/svgtest',
    element: <SVGTest />
  },
  {
    path: '/2fa-setup',
    element: isLoggedIn && is2FAAuthed ? <Navigate to="/launcher" /> : isLoggedIn ? <TwoFactorAuth /> : <Navigate to="/login" />
  }
];

export default routes;
